import type {Logger as PinoLogger} from 'pino';
import Pino from 'pino';

import {
    createTextRingBuffer,
    type OnSegmentSealed,
    type TextRingBuffer,
} from '@pexip/utils';

import {type Logger, LogLevels, type LogLevelsString} from './baseLogger';
import {createRedactor, type Redactor} from './redactor';

export * from './redactor';

const REDACTED_REPLACEMENT = '[REDACTED]';

/**
 * Default amount of memory, in bytes, used to retain log events.
 */
const DEFAULT_MAX_LOG_BYTES = 4 * 1024 * 1024;
/**
 * Default maximum number of log events retained in memory.
 */
const DEFAULT_MAX_LOG_LINES = 16384;
/**
 * Number of segments the log memory is split into. Log events are dropped a
 * segment at a time, so this is also the granularity at which memory is
 * reclaimed.
 */
const LOG_SEGMENT_COUNT = 8;
const textEncoder = new TextEncoder();
const MIN_TRUNCATED_LOG_EVENT = '{"logEventTruncated":true}\n';
const MIN_LOG_BYTE_BUDGET =
    textEncoder.encode(MIN_TRUNCATED_LOG_EVENT).byteLength * LOG_SEGMENT_COUNT;
const SERIALIZATION_FAILURE_MESSAGE = 'Log event could not be serialized';
const REDACTION_INCOMPLETE_MESSAGE = 'Log event withheld: redaction incomplete';
/**
 * Top-level fields every consumer reads to place an event: its severity, time,
 * message and logger name. Redaction must not rename them.
 */
const REQUIRED_EVENT_FIELDS: ReadonlySet<string> = new Set([
    'level',
    'time',
    'msg',
    'name',
]);
/**
 * Replaces an object field of an event that could not be serialized. The
 * object is never converted to a string, because its `toString` could return
 * values that redaction removed.
 */
const UNSERIALIZABLE_VALUE = '[Unserializable]';
/**
 * Default largest event, in UTF-8 bytes including the newline, a
 * {@link LogEventSink} receives as it is.
 */
const DEFAULT_MAX_SINK_EVENT_BYTES = 64 * 1024;
const MIN_SINK_EVENT_BYTES = textEncoder.encode(
    MIN_TRUNCATED_LOG_EVENT,
).byteLength;

/**
 * Fixed bounds on delivery to a {@link LogEventSink}; not configurable.
 */
export const SINK_DELIVERY_LIMITS = Object.freeze({
    /**
     * Events delivered to the sink in one task. Later events of the same task
     * are dropped and counted, and the count is delivered as one summary
     * event at the start of the next task. This bounds a sink that logs
     * through the same logger after an `await`, which would otherwise feed
     * itself in one endless chain of microtasks.
     */
    maxEventsPerTask: 1024,
});
const SINK_DROPPED_MESSAGE = 'Log events were dropped before the sink';

/**
 * Runs `callback` in a later task. A message on a new `MessageChannel` is not
 * delayed by the timer throttling of hidden pages, so the task follows soon
 * after the current one; `setTimeout` is the fallback where channels are
 * unavailable.
 *
 * @example
 * runInNextTask(() => resetBudget());
 */
const runInNextTask = (callback: () => void): void => {
    if (typeof MessageChannel !== 'function') {
        setTimeout(callback, 0);
        return;
    }
    const channel = new MessageChannel();
    channel.port1.onmessage = () => {
        channel.port1.close();
        callback();
    };
    channel.port2.postMessage(undefined);
};

/**
 * Holds an own `__proto__` field while pino merges logging arguments with
 * `Object.assign`, which would otherwise set the event's prototype instead of
 * copying the field.
 */
const PROTO_FIELD = Symbol('__proto__');

/**
 * `value` with an own `__proto__` field moved to {@link PROTO_FIELD}, or
 * `value` itself when it has none.
 *
 * @example
 * escapeProtoField(JSON.parse('{"__proto__": 1}')); // {[PROTO_FIELD]: 1}
 */
const escapeProtoField = <T>(value: T): T => {
    if (
        typeof value !== 'object' ||
        value === null ||
        Array.isArray(value) ||
        !Object.hasOwn(value, '__proto__')
    ) {
        return value;
    }
    // Spreading defines `__proto__` as an own field, and so does reading it
    // back: the own field shadows the accessor on `Object.prototype`.
    const escaped: Record<string | symbol, unknown> = {
        ...(value as Record<string, unknown>),
    };
    escaped[PROTO_FIELD] = Object.getOwnPropertyDescriptor(
        escaped,
        '__proto__',
    )?.value;
    Reflect.deleteProperty(escaped, '__proto__');
    return escaped as T;
};

/**
 * Defines `value` as the own data field `name` of `target`, so a field named
 * `__proto__` stays a field rather than setting the prototype.
 *
 * @example
 * defineField(copy, '__proto__', value);
 */
const defineField = (target: object, name: string, value: unknown): void => {
    Object.defineProperty(target, name, {
        value,
        enumerable: true,
        writable: true,
        configurable: true,
    });
};

/**
 * Moves {@link PROTO_FIELD} of an event back to an own `__proto__` field.
 *
 * @example
 * restoreProtoField(event); // event.__proto__ is now an own data field
 */
const restoreProtoField = (event: Record<string | symbol, unknown>): void => {
    if (Object.hasOwn(event, PROTO_FIELD)) {
        defineField(event, '__proto__', event[PROTO_FIELD]);
        delete event[PROTO_FIELD];
    }
};

export type {Logger, LogLevelsString};
export {LogLevels};
export interface ChildLogger extends Logger {
    child(bindings: Record<string, unknown>): Logger;
}

export interface PexLogger extends ChildLogger {
    /**
     * Set this property to the desired logging level. In order of priority, available levels are:
        1. trace
        2. debug
        3. info
        4. warn
        5. error
        6. fatal
        7. silent

        The logging level is a minimum level. For instance if logger.level is 'info' then all 'fatal', 'error', 'warn', and 'info' logs will be enabled.

        You can pass 'silent' to disable logging.
     */
    level: string;
    /**
     * Get the log file as a blob
     */
    getLogs(): Blob;
    /**
     * Get a file name based on (app) name, current date/time, and extension
     */
    getLogFileName(): string;
    /**
     * Get the log file as a string.
     */
    getLogFile(): string;
    /**
     * The log events retained in memory.
     *
     * The buffer is bounded, see `maxLogBytes` and `maxLogLines`, so the oldest
     * events are dropped once it is full. It can be read lazily, one event at a
     * time, by iterating over it.
     **/
    logEvents: TextRingBuffer;
    /**
     * Create a log file based on the log until now, and trigger a download
     */
    downloadLog(): void;
}

/**
 * Receives every log event at or above the logger's level, after it was
 * redacted and serialized, so it never sees an unredacted value.
 */
export interface LogEventSink {
    /**
     * Called synchronously for each event with a fresh, JSON-safe copy of it:
     * structured keys and registered patterns are redacted in every string and
     * field name, and an event larger than {@link maxEventBytes} is replaced
     * with truncation metadata. A value that throws or a promise that rejects
     * is ignored, and events the sink itself logs while it runs are not sent
     * back to it. Events it logs later, after an `await`, are sent back, so a
     * sink must not log through the same logger asynchronously; delivery is
     * bounded per task by {@link SINK_DELIVERY_LIMITS} so that such a sink
     * cannot starve the page.
     *
     * @example
     * createLogger({
     *     logEventSink: {send: event => forward(event)},
     * });
     */
    send(event: Readonly<Record<string, unknown>>): void | PromiseLike<void>;
    /**
     * The largest serialized event, in UTF-8 bytes including its newline, that
     * is delivered as it is.
     * @defaultValue 65536 (64 KiB)
     */
    maxEventBytes?: number;
}

export function stringifyReplacer(_key: string, value: unknown) {
    if (value instanceof HTMLElement) {
        const innerHTML = value.innerHTML;
        if (innerHTML !== '') {
            return value.outerHTML.replace(innerHTML, '{...}');
        } else {
            return value.outerHTML;
        }
    } else if (value instanceof Function) {
        return `[Function ${value.name}]`;
    }
    return value;
}

interface Options {
    /**
     * The name of the logger. When set adds a name field to every JSON line logged
     */
    name?: string;
    /**
     * Minimum level to be logged
     */
    minLevel?: LogLevelsString;
    /**
     * Name to prefix the file with for the output log file
     * @defaultValue 'pexip'
     */
    fileName?: string;
    /**
     * Extension (without the dot) to use for the output log file
     * @defaultValue 'log'
     */
    fileExt?: string;

    redactor?: Redactor;

    /**
     * Maximum amount of memory, in bytes, used to retain log events. Once
     * reached, the oldest events are dropped.
     *
     * The budget is split into equally sized blocks which are dropped as a
     * whole, so a single event larger than an eighth of it is replaced with a
     * bounded JSON record containing truncation metadata.
     *
     * Set to `0` to not retain log events at all, see {@link maxLogLines}.
     * @defaultValue 4194304 (4 MiB)
     */
    maxLogBytes?: number;
    /**
     * Maximum number of log events retained in memory. Once reached, the oldest
     * events are dropped.
     *
     * Set this or {@link maxLogBytes} to `0` to retain nothing: no buffer is
     * allocated, `onLogSegmentSealed` is never called, `logEvents` behaves as
     * a permanently empty buffer, and `downloadLog` produces an empty file.
     * Without a {@link logEventSink}, events are then only written to the
     * console and are neither serialized nor redacted.
     * @defaultValue 16384
     */
    maxLogLines?: number;
    /**
     * Called when a block of log events is about to leave the active part of
     * the buffer, which is the extension seam for persisting them elsewhere.
     * @see {@link OnSegmentSealed}
     */
    onLogSegmentSealed?: OnSegmentSealed;
    /**
     * Receives every redacted event, independently of the retained log, so a
     * consumer can persist events elsewhere even when none are retained.
     * @see {@link LogEventSink}
     */
    logEventSink?: LogEventSink;
    /**
     * Wraps every logging call, so a host that intercepts `console` can tell
     * the logger's own console output from other console calls. It must call
     * `output` exactly once, synchronously.
     */
    consoleOutputGuard?: (output: () => void) => void;
}

export const isLogLevelKey = (t: string | undefined): t is LogLevelsString =>
    typeof t === 'string' && t in LogLevels;

const assertLogBudget = (
    name: string,
    value: number,
    minimum = LOG_SEGMENT_COUNT,
) => {
    if (!Number.isInteger(value) || (value !== 0 && value < minimum)) {
        throw new Error(
            `${name} must be 0, to not retain log events, or an integer of at least ${minimum}`,
        );
    }
};

const createTruncatedLogEvent = (
    event: Record<string, unknown>,
    originalByteLength: number,
    maximumByteLength: number,
) => {
    const level = typeof event.level === 'number' ? event.level : undefined;
    const time = typeof event.time === 'number' ? event.time : undefined;
    const replacement = `${JSON.stringify({
        level,
        time,
        msg: 'Log event exceeded maximum size',
        logEventTruncated: true,
        originalByteLength,
        maximumByteLength,
    })}\n`;

    return textEncoder.encode(replacement).byteLength <= maximumByteLength
        ? replacement
        : MIN_TRUNCATED_LOG_EVENT;
};

const utf8ByteLength = (value: string) => {
    let byteLength = 0;
    for (let index = 0; index < value.length; index++) {
        const codeUnit = value.charCodeAt(index);
        if (codeUnit <= 0x7f) {
            byteLength += 1;
        } else if (codeUnit <= 0x7ff) {
            byteLength += 2;
        } else if (
            codeUnit >= 0xd800 &&
            codeUnit <= 0xdbff &&
            index + 1 < value.length
        ) {
            const nextCodeUnit = value.charCodeAt(index + 1);
            if (nextCodeUnit >= 0xdc00 && nextCodeUnit <= 0xdfff) {
                byteLength += 4;
                index += 1;
                continue;
            }
            byteLength += 3;
        } else {
            byteLength += 3;
        }
    }
    return byteLength;
};

/**
 * Creates a buffer which discards everything written to it, used when log
 * events are not retained so that `logEvents` keeps its shape without
 * allocating any memory.
 *
 * Example usage:
 *
 * ```ts
 * const logEvents = createDiscardingLogEvents();
 * logEvents.push('{"msg":"hello"}\n');
 * logEvents.isEmpty(); // true
 * ```
 *
 * @returns An empty, immutable {@link TextRingBuffer}
 */
const createDiscardingLogEvents = (): TextRingBuffer => ({
    push: () => undefined,
    size: () => 0,
    byteLength: () => 0,
    isEmpty: () => true,
    droppedLines: () => 0,
    truncatedLines: () => 0,
    clear: () => undefined,
    flush: () => undefined,
    toBlobParts: () => [],
    toString: () => '',
    toArray: () => [],
    [Symbol.iterator]: () => [][Symbol.iterator](),
});

/**
 * Which field names redaction must leave unchanged: those in a set, or every
 * field name, for a value whose field names are a fixed schema.
 */
type RequiredFieldNames = ReadonlySet<string> | 'every-field';

/**
 * Applies the registered runtime patterns to the field names of `value`.
 * Returns `value` itself when no name changes.
 *
 * @throws When two names redact to the same name, which would silently drop a
 * field, or when a `required` name would be renamed or produced; the caller
 * then records a generic failure or drops the item instead.
 *
 * @example
 * redactFieldNames({'id-secret': 1}, redactor.redactStr, new Set(['level']));
 * // {'id-[REDACTED]': 1}
 */
const redactFieldNames = (
    value: object,
    redactStr: (text: string) => string,
    required: RequiredFieldNames,
): object => {
    const entries = Object.entries(value);
    const names = entries.map(([name]) => redactStr(name));
    if (names.every((name, index) => name === entries[index]?.[0])) {
        return value;
    }
    const isRequired = (name: string) =>
        required === 'every-field' || required.has(name);
    const redacted: Record<string, unknown> = {};
    entries.forEach(([original, field], index) => {
        const name = names[index] ?? '';
        if (name !== original && (isRequired(original) || isRequired(name))) {
            throw new Error('Redaction would alter a required field');
        }
        if (Object.hasOwn(redacted, name)) {
            throw new Error('Redacted field names collide');
        }
        defineField(redacted, name, field);
    });
    return redacted;
};

const NO_REQUIRED_FIELDS: ReadonlySet<string> = new Set();

/**
 * Creates a `JSON.stringify` replacer for one call, which applies the
 * registered runtime patterns to every string and field name, after
 * `transform`. Field names of the root object in `requiredRootFields` must
 * keep their names, and with `fixedFieldNames` every field name must.
 *
 * @example
 * JSON.stringify(event, createPatternReplacer({redactStr: redactor.redactStr}));
 */
const createPatternReplacer = ({
    redactStr,
    transform = (_key, value) => value,
    requiredRootFields = NO_REQUIRED_FIELDS,
    fixedFieldNames = false,
}: {
    redactStr: (text: string) => string;
    transform?: (key: string, value: unknown) => unknown;
    requiredRootFields?: ReadonlySet<string>;
    fixedFieldNames?: boolean;
}) => {
    // `JSON.stringify` calls the replacer for the root value first.
    let atRoot = true;
    return (key: string, input: unknown): unknown => {
        const value = transform(key, input);
        const required: RequiredFieldNames = fixedFieldNames
            ? 'every-field'
            : atRoot
              ? requiredRootFields
              : NO_REQUIRED_FIELDS;
        atRoot = false;
        if (typeof value === 'string') {
            return redactStr(value);
        }
        if (
            typeof value === 'object' &&
            value !== null &&
            !Array.isArray(value)
        ) {
            return redactFieldNames(value, redactStr, required);
        }
        return value;
    };
};

const isPromiseLike = (value: unknown): value is PromiseLike<unknown> =>
    typeof value === 'object' &&
    value !== null &&
    'then' in value &&
    typeof value.then === 'function';

const stringifyLogEvent = (
    event: Record<string, unknown>,
    replacer?: (key: string, value: unknown) => unknown,
) => {
    const serializableEvent =
        typeof event.toJSON === 'function'
            ? Object.fromEntries(
                  Object.entries(event).filter(([key]) => key !== 'toJSON'),
              )
            : event;
    const serialized = JSON.stringify(serializableEvent, replacer);
    return typeof serialized === 'string' ? serialized : undefined;
};

/**
 * A finite number read from `event[name]`, or `undefined`.
 *
 * @example
 * readNumber({level: 30}, 'level'); // 30
 * readNumber({level: '30'}, 'level'); // undefined
 */
const readNumber = (
    event: Record<string, unknown>,
    name: string,
): number | undefined => {
    try {
        const value = event[name];
        return typeof value === 'number' && Number.isFinite(value)
            ? value
            : undefined;
    } catch {
        return undefined;
    }
};

/**
 * A generic event that keeps only the severity and time of `event` and a fixed
 * message, so a consumer still files it at the original level.
 *
 * @example
 * genericEventLine({level: 60, time: 1, token: 'x'}, 'Log event could not be serialized');
 * // '{"level":60,"time":1,"msg":"Log event could not be serialized"}'
 */
const genericEventLine = (event: Record<string, unknown>, msg: string) =>
    JSON.stringify({
        level: readNumber(event, 'level'),
        time: readNumber(event, 'time'),
        msg,
    });

/** One event as a redacted JSON line, newline included. */
interface SerializedLine {
    line: string;
    byteLength: number;
    /** The key-redacted event, for truncation metadata. */
    redactedEvent: Record<string, unknown>;
}

/**
 * Creates the function that turns an event into one redacted JSON line:
 * structured keys, then registered patterns in every string and field name.
 * An event that cannot be serialized, whose redacted field names collide, or
 * whose required fields would be renamed becomes a generic failure line with
 * its level and time. While redaction is incomplete, every event becomes a
 * generic line that withholds everything but its level and time.
 */
const createLineSerializer = (redactor: Redactor) => {
    /**
     * One serialized event as a line with its byte length.
     *
     * @example
     * toLine('{"msg":"m"}', {msg: 'm'}); // {line: '{"msg":"m"}\n', byteLength: 12, ...}
     */
    const toLine = (
        serialized: string,
        redactedEvent: Record<string, unknown>,
    ) => {
        const line = `${serialized}\n`;
        return {line, byteLength: utf8ByteLength(line), redactedEvent};
    };

    return (event: Record<string, unknown>): SerializedLine => {
        if (redactor.isRedactionIncomplete()) {
            return toLine(
                genericEventLine(event, REDACTION_INCOMPLETE_MESSAGE),
                {},
            );
        }
        let redactedEvent: Record<string, unknown>;
        let serialized: string | undefined;
        try {
            redactedEvent = redactor.redactObj(event);
            serialized = stringifyLogEvent(
                redactedEvent,
                createPatternReplacer({
                    redactStr: redactor.redactStr,
                    transform: stringifyReplacer,
                    requiredRootFields: REQUIRED_EVENT_FIELDS,
                }),
            );
        } catch {
            serialized = undefined;
            redactedEvent = {};
        }
        if (serialized === undefined) {
            try {
                redactedEvent = redactor.redactObj(
                    Object.fromEntries(
                        Object.entries(event).map(([key, value]) => {
                            if (typeof value !== 'object' || value === null) {
                                return [key, value];
                            }
                            try {
                                const field = redactor.redactObj({
                                    [key]: value,
                                });
                                JSON.stringify(field);
                                return [key, field[key]];
                            } catch {
                                return [key, UNSERIALIZABLE_VALUE];
                            }
                        }),
                    ),
                );
                serialized = stringifyLogEvent(
                    redactedEvent,
                    createPatternReplacer({
                        redactStr: redactor.redactStr,
                        requiredRootFields: REQUIRED_EVENT_FIELDS,
                    }),
                );
            } catch {
                serialized = undefined;
                redactedEvent = {};
            }
        }
        return toLine(
            serialized ??
                genericEventLine(event, SERIALIZATION_FAILURE_MESSAGE),
            redactedEvent,
        );
    };
};

/** The line itself, or its truncation metadata when it is larger than `maximum`. */
const boundedLine = (
    {line, byteLength, redactedEvent}: SerializedLine,
    maximum: number,
) =>
    byteLength <= maximum
        ? line
        : createTruncatedLogEvent(redactedEvent, byteLength, maximum);

const assertMaxEventBytes = (maxEventBytes: number) => {
    if (
        !Number.isInteger(maxEventBytes) ||
        maxEventBytes < MIN_SINK_EVENT_BYTES
    ) {
        throw new Error(
            `maxEventBytes must be an integer of at least ${MIN_SINK_EVENT_BYTES}`,
        );
    }
};

/**
 * Creates the function that redacts one event exactly as a logger does before
 * it reaches a {@link LogEventSink}, for events that do not come from a
 * logger, such as copies of console calls.
 *
 * @example
 * const redactEvent = createLogEventRedactor({redactor});
 * redactEvent({level: 30, time: Date.now(), msg: 'Started', token: 'secret'});
 * // {level: 30, time: ..., msg: 'Started', token: '[REDACTED]'}
 */
export const createLogEventRedactor = ({
    redactor,
    maxEventBytes = DEFAULT_MAX_SINK_EVENT_BYTES,
}: {
    redactor: Redactor;
    /**
     * The largest serialized event, in UTF-8 bytes including its newline,
     * returned as it is; a larger one becomes truncation metadata.
     * @defaultValue 65536 (64 KiB)
     */
    maxEventBytes?: number;
}): ((event: Record<string, unknown>) => Readonly<Record<string, unknown>>) => {
    assertMaxEventBytes(maxEventBytes);
    const serialize = createLineSerializer(redactor);

    return event =>
        JSON.parse(boundedLine(serialize(event), maxEventBytes)) as Record<
            string,
            unknown
        >;
};

/**
 * Creates the function that redacts a structured diagnostic that is not a log
 * event, such as an item for an error-reporting service: structured keys, then
 * registered patterns in every string. Its field names are taken to be a fixed
 * schema, so a registered pattern that would rename any field drops the whole
 * item rather than producing an invalid shape. It applies no size limit and
 * returns a fresh JSON-safe copy.
 *
 * @returns `undefined` when the value cannot be redacted, because a field
 * name would change or it cannot be serialized, and while redaction is
 * incomplete; the caller then drops the item instead of sending it.
 *
 * @example
 * const redactDiagnostic = createDiagnosticRedactor({redactor});
 * redactDiagnostic({message: 'Failed', extra: {token: 'secret'}});
 * // {message: 'Failed', extra: {token: '[REDACTED]'}}
 */
export const createDiagnosticRedactor =
    ({redactor}: {redactor: Redactor}) =>
    (value: object): Record<string, unknown> | undefined => {
        if (redactor.isRedactionIncomplete()) {
            return undefined;
        }
        try {
            const serialized = stringifyLogEvent(
                redactor.redactObj(value as Record<string, unknown>),
                createPatternReplacer({
                    redactStr: redactor.redactStr,
                    transform: stringifyReplacer,
                    fixedFieldNames: true,
                }),
            );
            if (serialized === undefined || redactor.isRedactionIncomplete()) {
                return undefined;
            }
            const parsed: unknown = JSON.parse(serialized);
            return typeof parsed === 'object' &&
                parsed !== null &&
                !Array.isArray(parsed)
                ? (parsed as Record<string, unknown>)
                : undefined;
        } catch {
            return undefined;
        }
    };

export const createLogger = ({
    minLevel = 'info',
    fileName = 'pexip',
    fileExt = 'log',
    maxLogBytes = DEFAULT_MAX_LOG_BYTES,
    maxLogLines = DEFAULT_MAX_LOG_LINES,
    onLogSegmentSealed,
    logEventSink,
    consoleOutputGuard = output => output(),
    redactor = createRedactor({
        defaultReplaceTo: REDACTED_REPLACEMENT,
        keysToRedact: [],
    }),
}: Options = {}): PexLogger => {
    assertLogBudget('maxLogBytes', maxLogBytes, MIN_LOG_BYTE_BUDGET);
    assertLogBudget('maxLogLines', maxLogLines);
    const maxSinkEventBytes =
        logEventSink?.maxEventBytes ?? DEFAULT_MAX_SINK_EVENT_BYTES;
    assertMaxEventBytes(maxSinkEventBytes);

    const retainLogEvents = maxLogBytes > 0 && maxLogLines > 0;
    const serializeLogEvents = retainLogEvents || logEventSink !== undefined;
    const serializeLine = createLineSerializer(redactor);
    let sendingToSink = false;

    /**
     * Delivers one event to the sink. Events the sink logs while it runs are
     * retained but not sent back to it; events it logs later, after an
     * `await`, are bounded by {@link SINK_DELIVERY_LIMITS}.
     *
     * @example
     * deliverToSink({level: 30, time: Date.now(), msg: 'm'});
     */
    const deliverToSink = (event: Record<string, unknown>) => {
        if (!logEventSink || sendingToSink) {
            return;
        }
        sendingToSink = true;
        try {
            const sent: unknown = logEventSink.send(event);
            if (isPromiseLike(sent)) {
                sent.then(undefined, () => undefined);
            }
        } catch {
            // A failing sink must not interrupt logging.
        } finally {
            sendingToSink = false;
        }
    };
    let deliveredThisTask = 0;
    let droppedForSink = 0;
    let nextTaskScheduled = false;
    /**
     * Starts a new task's budget and reports what the last one dropped.
     *
     * @example
     * runInNextTask(startNextTask);
     */
    const startNextTask = () => {
        nextTaskScheduled = false;
        deliveredThisTask = 0;
        if (droppedForSink > 0) {
            const count = droppedForSink;
            droppedForSink = 0;
            deliveredThisTask += 1;
            deliverToSink({
                level: LogLevels.warn,
                time: Date.now(),
                msg: SINK_DROPPED_MESSAGE,
                count,
            });
        }
    };
    /**
     * Sends one serialized event to the sink, within the per-task budget.
     *
     * @example
     * sendToSink('{"level":30,"msg":"m"}\n');
     */
    const sendToSink = (logLine: string) => {
        if (!logEventSink || sendingToSink) {
            return;
        }
        if (!nextTaskScheduled) {
            nextTaskScheduled = true;
            runInNextTask(startNextTask);
        }
        if (deliveredThisTask >= SINK_DELIVERY_LIMITS.maxEventsPerTask) {
            droppedForSink += 1;
            return;
        }
        deliveredThisTask += 1;
        deliverToSink(JSON.parse(logLine) as Record<string, unknown>);
    };
    const maxLogEventBytes = Math.floor(maxLogBytes / LOG_SEGMENT_COUNT);
    const logEvents = retainLogEvents
        ? createTextRingBuffer({
              segmentByteSize: maxLogEventBytes,
              segmentCount: LOG_SEGMENT_COUNT,
              maxLinesPerSegment: Math.floor(maxLogLines / LOG_SEGMENT_COUNT),
              onSegmentSealed: onLogSegmentSealed,
          })
        : createDiscardingLogEvents();

    function getLogs() {
        return new Blob(
            logEvents.toBlobParts(),
            // Technically application/ld+json
            {type: 'text/plain; charset=utf-8'},
        );
    }

    function getLogFile(): string {
        return logEvents.toString();
    }

    function getLogFileName(name = fileName, ext = fileExt) {
        return `${name}-${new Date().toISOString().replace(/:/g, '-')}.${ext}`;
    }

    function downloadLog(fileName = getLogFileName()) {
        const blobUrl = URL.createObjectURL(getLogs());
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.click();
        // Wait half a second, as reportedly some browsers will fail if it's revoked too quickly
        setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
    }

    // HACK: Stupid solution, but seems to be the best way to transform
    //       transmit format to what e.g. pino-pretty expects.
    const pinoStore = Pino({
        browser: {
            write: (evt: unknown) => {
                if (typeof evt !== 'object' || !evt) {
                    return;
                }
                restoreProtoField(evt as Record<string | symbol, unknown>);
                const serialized = serializeLine(
                    evt as Record<string, unknown>,
                );
                if (retainLogEvents) {
                    const retained = boundedLine(serialized, maxLogEventBytes);
                    logEvents.push(retained, retained !== serialized.line);
                }
                sendToSink(boundedLine(serialized, maxSinkEventBytes));
            },
        },
        level: minLevel,
    });

    const pino = Pino({
        // Without `transmit` pino only writes to the console, which is exactly
        // what is wanted when events are neither retained nor sent to a sink.
        browser: serializeLogEvents
            ? {
                  transmit: {
                      send: (level, event) => {
                          const [first, ...rest] =
                              event.messages.map(escapeProtoField); // work around ts typechecking
                          event.bindings
                              .map(escapeProtoField)
                              .reduce<Pino.Logger>(
                                  (ch, bi) => ch.child(bi),
                                  pinoStore,
                              )
                              [level](first, ...rest);
                      },
                  },
              }
            : {},
        level: minLevel,
    });

    const addPattern = (value: string, redactTo = REDACTED_REPLACEMENT) =>
        redactor.addPattern(value, redactTo);

    return {
        get logEvents() {
            return logEvents;
        },
        get level() {
            return pino.level;
        },
        set level(level: typeof pino.level) {
            pinoStore.level = level;
            pino.level = level;
        },
        getLogs,
        getLogFileName,
        getLogFile,
        downloadLog,
        addRedactPattern: addPattern,
        redact: redactor.redactStr,
        fatal: (meta, message) =>
            consoleOutputGuard(() => pino.fatal(meta, message)),
        error: (meta, message) =>
            consoleOutputGuard(() => pino.error(meta, message)),
        warn: (meta, message) =>
            consoleOutputGuard(() => pino.warn(meta, message)),
        info: (meta, message) =>
            consoleOutputGuard(() => pino.info(meta, message)),
        debug: (meta, message) =>
            consoleOutputGuard(() => pino.debug(meta, message)),
        trace: (meta, message) =>
            consoleOutputGuard(() => pino.trace(meta, message)),
        silent: (meta, message) =>
            consoleOutputGuard(() => pino.silent(meta, message)),
        child: (...args: Parameters<PinoLogger['child']>) => {
            const child = pino.child(...args);
            return {
                fatal: (meta, message) =>
                    consoleOutputGuard(() => child.fatal(meta, message)),
                error: (meta, message) =>
                    consoleOutputGuard(() => child.error(meta, message)),
                warn: (meta, message) =>
                    consoleOutputGuard(() => child.warn(meta, message)),
                info: (meta, message) =>
                    consoleOutputGuard(() => child.info(meta, message)),
                debug: (meta, message) =>
                    consoleOutputGuard(() => child.debug(meta, message)),
                trace: (meta, message) =>
                    consoleOutputGuard(() => child.trace(meta, message)),
                silent: (meta, message) =>
                    consoleOutputGuard(() => child.silent(meta, message)),
                redact: redactor.redactStr,
                addRedactPattern: addPattern,
            };
        },
    };
};
